import { GeneralGlobeIcon } from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import downloadIOSAppImage from '@/assets/footer/appstore.png';
import downloadAndroidAppImage from '@/assets/footer/googleplay.png';
import Anchor from '@/components/switchback/Anchor/Anchor';
import { LanguageAndCurrencyModal } from '@/components/ui/GlobalHeader/GlobalHeaderV2/LanguageAndCurrencyModal';
import { openZendeskChat } from '@/components/ZendeskChat/ZendeskChat';
import { useRideEntryPointImpression } from '@/hooks/useRideEntryPointImpression';
import { useRouter } from '@/hooks/useRouter';
import { useShowRideLinks } from '@/hooks/useShowRideLinks';
import { EFeedbackEventSource, trackFeedbackCTASelectedEvent } from '@/services/analytics/feedback';
import {
  EGiftCardsEventSource,
  EGroupBookingsEventSource,
  trackGiftCardsSelectedEvent,
  trackGroupBookingsSelectedEvent,
} from '@/services/analytics/footer';
import { parsePageMetaData } from '@/services/analytics/pageEventUtils';
import {
  ERideTransitionPlacement,
  trackRideTransitionSelectedEvent,
} from '@/services/analytics/ride';
import { getRideBaseUrl } from '@/utility/getRideBaseUrl';

import styles from './Footer.module.css';
import FooterListItem from './FooterListItem';
import FacebookIcon from './icons/facebook.svg';
import InstagramIcon from './icons/instagram.svg';
import LinkedInIcon from './icons/linkedIn.svg';
import PinterestIcon from './icons/pinterest.svg';
import YouTubeIcon from './icons/youtube.svg';
import { PrivacyChoices } from './PrivacyChoices';

type FooterProps = {
  isNewLayout?: boolean;
};

const Footer: React.FC<FooterProps> = ({ isNewLayout }) => {
  const intl = useIntl();
  const router = useRouter();
  const showRideLinks = useShowRideLinks();
  const rideLinkImpressionRef = useRideEntryPointImpression(
    showRideLinks
      ? { placement: ERideTransitionPlacement.FOOTER, destinationUrl: getRideBaseUrl() }
      : null,
  );
  const [isLanguageCurrencyModalOpen, setIsLanguageCurrencyModalOpen] = useState(false);

  const giveUsFeedback = () => {
    trackFeedbackCTASelectedEvent({
      pageCategory: parsePageMetaData(window.sessionStorage).pageCategory,
      source: EFeedbackEventSource.FOOTER,
    });
  };

  const giftCardsHandler = () => {
    trackGiftCardsSelectedEvent({ source: EGiftCardsEventSource.FOOTER });
    router.push('/gift-cards');
  };

  const bookingHandler = () => {
    trackGroupBookingsSelectedEvent({ source: EGroupBookingsEventSource.FOOTER });
    router.push('/group-rv-bookings');
  };

  // Footer columns and link labels
  // Labels found underneath Outdoorsy, Inc copyright label
  const weAreHereForGood = intl.formatMessage({
    defaultMessage: 'We are here for good ✨',
    id: 'NARZ1g',
  });
  const linkLabelTerms = intl.formatMessage({ defaultMessage: 'Terms', id: 'xkr+zo' });
  const linkLabelPrivacy = intl.formatMessage({ defaultMessage: 'Privacy', id: 'cXBJ7U' });
  const linkLabelSiteMap = intl.formatMessage({ defaultMessage: 'Sitemap', id: '/xvc73' });
  // Download Outdoorsy app
  const downloadOutdoorsyAppTitle = intl.formatMessage({
    defaultMessage: 'Download Outdoorsy app',
    id: 'tHXrtD',
  });
  const linkLabelIOSApp = intl.formatMessage({ defaultMessage: 'iOS app', id: 'I+K2Q0' });
  const linkLabelAndroidApp = intl.formatMessage({ defaultMessage: 'Android app', id: 'TKazAi' });
  // Outdoorsy Column
  const columnTitleOutdoorsy = intl.formatMessage({ defaultMessage: 'Outdoorsy', id: 'E/gN7p' });
  // const linkLabelWhereItAllBegan = intl.formatMessage({
  //   defaultMessage: 'Where it all began',
  //   id: 'qoncDn',
  // });
  const linkLabelAbout = intl.formatMessage({ defaultMessage: 'Where it all began', id: 'qoncDn' });
  const linkLabelCareers = intl.formatMessage({ defaultMessage: 'Careers', id: 'yJ1ORH' });
  const linkLabelStoriesAndNews = intl.formatMessage({
    defaultMessage: 'Stories and news',
    id: 'ocaCIr',
  });
  const linkLabelTravelJournal = intl.formatMessage({
    defaultMessage: 'Travel journal',
    id: 'zVsWEO',
  });
  const linkLabelOutdoorsyGroup = intl.formatMessage({
    defaultMessage: 'About',
    id: 'g5pX+a',
  });
  // Guest Travel Column
  const columnTitleGuestTravel = intl.formatMessage({
    defaultMessage: 'Guest travel',
    id: 'CaXvyh',
  });
  const linkLabelGroupBookings = intl.formatMessage({
    defaultMessage: 'Group bookings',
    id: 'LR5CEy',
  });
  const linkLabelGiftCards = intl.formatMessage({ defaultMessage: 'Gift cards', id: 'XIWEFl' });
  const linkLabelDelivery = intl.formatMessage({ defaultMessage: 'Delivery', id: 'drqP2L' });
  const linkLabelCarRentals = intl.formatMessage({
    id: '6aBsrg',
    defaultMessage: 'Car rentals',
    description: 'Global Footer > Guest travel column > Link to ride.auto car rental marketplace',
  });
  const linkLabelNationalParkGuides = intl.formatMessage({
    defaultMessage: 'National park guides',
    id: 'NNbJ4C',
  });
  const linkOneWayRentals = intl.formatMessage({ defaultMessage: 'One-way rentals', id: 'v1tv8x' });
  const linkLabelRoadTripGuides = intl.formatMessage({
    defaultMessage: 'Road trip guides',
    id: 'ewQUDn',
  });
  const linkLabelParkRVParksAndCampgrounds = intl.formatMessage({
    defaultMessage: 'RV parks & campgrounds',
    id: 'DXcgJo',
  });
  const linkLabelGuideToAllRVTypes = intl.formatMessage({
    defaultMessage: 'Guide to all RV types',
    id: '2n3c3O',
  });
  // Hosting Column
  const columnTitleHosting = intl.formatMessage({ defaultMessage: 'Hosting', id: 'DkzeEN' });
  const linkLabelListRV = intl.formatMessage({ defaultMessage: 'Become an RV host', id: 'etvePk' });
  const linkLabelWheelbaseDemo = intl.formatMessage({
    defaultMessage: 'Wheelbase demo',
    id: '0+w2gF',
  });
  const linkLabelAffiliateProgram = intl.formatMessage({
    defaultMessage: 'Affiliate program',
    id: '0nb2QR',
  });
  const linkLabelRVInsurance = intl.formatMessage({ defaultMessage: 'RV insurance', id: 'UZmRiJ' });
  const linkLabelHostIOSApp = intl.formatMessage({ defaultMessage: 'Host iOS app', id: '+LvbHE' });
  const linkLabelHostAndroidApp = intl.formatMessage({
    defaultMessage: 'Host Android app',
    id: 'H/I+rm',
  });
  // Support Column
  const columnTitleSupport = intl.formatMessage({ defaultMessage: 'Support', id: 'HqRNN8' });
  const linkLabelHowItWorks = intl.formatMessage({ defaultMessage: 'How it works', id: 'HK3ph8' });
  const linkLabelHelpCenter = intl.formatMessage({ defaultMessage: 'Help center', id: 'BYkLO/' });
  const linkLabelLLMInfo = intl.formatMessage({
    defaultMessage: 'LLM info',
    id: 'D/6a11',
    description: 'Global Footer > Support column > Link to AI/LLM information page',
  });

  return (
    <div id="footer" className={styles.footer} data-visible="true">
      <div
        className={`py-12 mx-auto ${isNewLayout ? 'px-6 max-w-xxxxl lg:px-18' : 'px-4 max-w-xxl'}`}
        data-test-application-layout-footer>
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          <div className="xl:col-span-1">
            <div className="flex flex-col mb-6">
              <button
                type="button"
                data-testid="footer-language-currency-trigger"
                className="flex items-center self-start text-base font-medium text-white gap-1.5 leading-6 hover:underline underline-offset-2"
                onClick={() => setIsLanguageCurrencyModalOpen(true)}>
                <GeneralGlobeIcon className="w-5 h-5" aria-hidden />
                <FormattedMessage
                  id="8syG9v"
                  defaultMessage="Language & currency"
                  description="Footer > Language & currency trigger"
                />
              </button>
              <LanguageAndCurrencyModal
                isOpen={isLanguageCurrencyModalOpen}
                onClose={() => setIsLanguageCurrencyModalOpen(false)}
              />
            </div>
            <div className="flex mt-6">
              <Anchor
                data-testid="footer-instagram-link"
                href="https://www.instagram.com/outdoorsy/"
                className="transition-all duration-200 hover:scale-110">
                <span className="sr-only">Instagram</span>
                <InstagramIcon className={styles.socialIcon} />
              </Anchor>
              <Anchor
                data-testid="footer-facebook-link"
                href="https://www.facebook.com/GoOutdoorsy/"
                className="ml-6 transition-all duration-200 hover:scale-110">
                <span className="sr-only">Facebook</span>
                <FacebookIcon className={styles.socialIcon} />
              </Anchor>
              <Anchor
                data-testid="footer-youtube-link"
                href="https://www.youtube.com/@OutdoorsyRV"
                className="ml-6 transition-all duration-200 hover:scale-110">
                <span className="sr-only">YouTube</span>
                <YouTubeIcon className={styles.socialIcon} />
              </Anchor>

              <Anchor
                data-testid="footer-pinterest-link"
                href="https://www.pinterest.com/GoOutdoorsy/"
                className="ml-6 transition-all duration-200 hover:scale-110">
                <span className="sr-only">Pinterest</span>
                <PinterestIcon className={styles.socialIcon} />
              </Anchor>
              <Anchor
                data-testid="footer-linkedin-link"
                href="https://www.linkedin.com/company/outdoorsy"
                className="ml-6 transition-all duration-200 hover:scale-110">
                <span className="sr-only">LinkedIn</span>
                <LinkedInIcon className={styles.socialIcon} />
              </Anchor>
            </div>

            <div className="mt-8 text-base text-white">
              <h3 className="text-base font-medium text-white leading-6">
                {downloadOutdoorsyAppTitle}
              </h3>

              <div className="flex mt-2 gap-2">
                <Anchor
                  data-testid="footer-renter-iOS-app-link"
                  target="_blank"
                  href="/apps/guest/ios">
                  <Image src={downloadIOSAppImage} alt={linkLabelIOSApp} width={120} height={35} />
                </Anchor>

                <Anchor
                  data-testid="footer-renter-android-app-link"
                  target="_blank"
                  href="/apps/guest/android">
                  <Image
                    src={downloadAndroidAppImage}
                    alt={linkLabelAndroidApp}
                    width={120}
                    height={35}
                  />
                </Anchor>
              </div>
            </div>
          </div>
          <div
            id="footer-links"
            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 xl:mt-0 xl:col-span-2">
            <div>
              <h3 className="text-lg font-medium text-canvas-100 leading-5">
                {columnTitleOutdoorsy}
              </h3>
              <ul>
                {/* <FooterListItem
                  dataTestId="footer-story-link"
                  href="/blog/outdoorsy-founder-story">
                  {linkLabelWhereItAllBegan}
                </FooterListItem> */}
                <FooterListItem dataTestId="footer-about-link" href="/about">
                  {linkLabelAbout}
                </FooterListItem>
                <FooterListItem dataTestId="footer-jobs-link" href="/jobs">
                  {linkLabelCareers}
                </FooterListItem>
                <FooterListItem dataTestId="footer-press-link" href="/press">
                  {linkLabelStoriesAndNews}
                </FooterListItem>
                <FooterListItem dataTestId="footer-blog-link" href="/blog">
                  {linkLabelTravelJournal}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-outdoorsy-group-link"
                  href="/about/outdoorsy-group">
                  {linkLabelOutdoorsyGroup}
                </FooterListItem>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium text-canvas-100 leading-5">
                {columnTitleGuestTravel}
              </h3>
              <ul className="mt-4">
                <FooterListItem dataTestId="footer-group-bookings-link" onClick={bookingHandler}>
                  {linkLabelGroupBookings}
                </FooterListItem>
                <FooterListItem dataTestId="footer-gift-cards-link" onClick={giftCardsHandler}>
                  {linkLabelGiftCards}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-rv-delivery-link"
                  href="https://www.outdoorsy.com/blog/what-is-rv-rental-delivery">
                  {linkLabelDelivery}
                </FooterListItem>
                {showRideLinks && (
                  <FooterListItem
                    dataTestId="footer-car-rentals-link"
                    anchorRef={rideLinkImpressionRef}
                    href={getRideBaseUrl()}
                    onClick={() =>
                      trackRideTransitionSelectedEvent({
                        placement: ERideTransitionPlacement.FOOTER,
                        destinationUrl: getRideBaseUrl(),
                      })
                    }>
                    {linkLabelCarRentals}
                  </FooterListItem>
                )}
                <FooterListItem
                  dataTestId="footer-park-link"
                  href="https://www.outdoorsy.com/guide/parks">
                  {linkLabelNationalParkGuides}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-one-way-link"
                  href="https://www.outdoorsy.com/rv-info/one-way-rv-rental">
                  {linkOneWayRentals}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-road-trips-link"
                  href="https://www.outdoorsy.com/guide/road-trips">
                  {linkLabelRoadTripGuides}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-rv-parks-link"
                  href="https://www.outdoorsy.com/rv-parks-and-campgrounds">
                  {linkLabelParkRVParksAndCampgrounds}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-rv-types-link"
                  href="https://www.outdoorsy.com/rv-makes-types">
                  {linkLabelGuideToAllRVTypes}
                </FooterListItem>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-medium text-canvas-100 leading-5">
                {columnTitleHosting}
              </h3>
              <ul className="mt-4">
                <FooterListItem dataTestId="footer-list-rv-link" href="/become-a-host">
                  {linkLabelListRV}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-wheelbase-link"
                  href="https://www.wheelbasepro.com">
                  {linkLabelWheelbaseDemo}
                </FooterListItem>
                <FooterListItem dataTestId="footer-affiliate-link" href="/affiliate">
                  {linkLabelAffiliateProgram}
                </FooterListItem>
                <FooterListItem dataTestId="footer-rv-insurance-link" href="https://www.roamly.com">
                  {linkLabelRVInsurance}
                </FooterListItem>
                <FooterListItem dataTestId="footer-owner-iOS-app-link" href="/apps/host/ios">
                  {linkLabelHostIOSApp}
                </FooterListItem>
                <FooterListItem
                  dataTestId="footer-owner-android-app-link"
                  href="/apps/host/android">
                  {linkLabelHostAndroidApp}
                </FooterListItem>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium text-canvas-100 leading-5">
                {columnTitleSupport}
              </h3>
              <ul className="mt-4">
                <FooterListItem dataTestId="footer-how-it-works-link" href="/how-it-works">
                  {linkLabelHowItWorks}
                </FooterListItem>
                <FooterListItem dataTestId="footer-help-link" href="/help">
                  {linkLabelHelpCenter}
                </FooterListItem>
                <li className="mt-4 truncate border-black">
                  <button
                    data-testid="live-chat-btn"
                    className="text-base text-white leading-6 hover:underline underline-offset-2"
                    onClick={openZendeskChat}>
                    {intl.formatMessage({
                      defaultMessage: 'Contact us',
                      id: '47T0OC',
                      description: 'Global Footer Item: Contact us',
                    })}
                  </button>
                </li>

                <li className="mt-4 truncate border-black">
                  <button
                    data-testid="live-chat-btn"
                    className="text-base text-white leading-6 hover:underline underline-offset-2"
                    onClick={giveUsFeedback}>
                    {intl.formatMessage({
                      defaultMessage: 'Give us feedback',
                      id: 'S8Z+hX',
                      description: 'Global Footer Item: Give us feedback button',
                    })}
                  </button>
                </li>
                <FooterListItem dataTestId="footer-llm-info-link" href="/llm-info">
                  {linkLabelLLMInfo}
                </FooterListItem>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div style={{ backgroundColor: 'var(--page-footer-bg-color)' }}>
        <div
          style={{ margin: 'auto', maxWidth: '1280px' }}
          className="flex flex-col px-4 py-2 text-sm md:justify-between md:flex-row gap-4">
          <div className="grow">{weAreHereForGood}</div>
          <div className="flex gap-2">
            <Anchor
              data-testid="footer-terms-and-conditions-link"
              className="hover:underline underline-offset-2"
              href="/terms-and-conditions">
              {linkLabelTerms}
            </Anchor>{' '}
            |{' '}
            <Anchor
              data-testid="footer-privacy-policy-link"
              className="hover:underline underline-offset-2"
              href="/privacy-policy">
              {linkLabelPrivacy}
            </Anchor>
            <PrivacyChoices /> |{' '}
            <Anchor
              data-testid="footer-sitemap-link"
              className="hover:underline underline-offset-2"
              href="/sitemap">
              {linkLabelSiteMap}
            </Anchor>
          </div>

          <div>© {new Date().getFullYear()} Outdoorsy, Inc. All rights reserved.</div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Footer);
